/**
 * Client-side CSV export helpers (no server round-trip).
 *
 * Mirrors the proven member-export pattern: every field is quoted, embedded
 * quotes are doubled, and a leading `= + - @` (or tab/CR) is prefixed with a
 * single quote to neutralize spreadsheet FORMULA INJECTION — a value like
 * `=cmd|...` must never be evaluated when the file is opened in Excel/Sheets.
 */

type CsvCell = string | number | null | undefined;

/** Escape one field: formula-injection guard + quote-wrap + quote-doubling. */
function escapeCell(value: CsvCell): string {
  const s = String(value ?? "");
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** Build a CSV string from a header row + data rows (CRLF line endings). */
export function buildCsv(header: string[], rows: CsvCell[][]): string {
  return [header, ...rows]
    .map((row) => row.map(escapeCell).join(","))
    .join("\r\n");
}

/** Trigger a browser download of `csv` as `filename` via an object URL. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
